import * as React from 'react'
import { DimensionOptionsPreview } from './DimensionOptionsPreview'
import { DimensionOptionsButtons } from './DimensionOptionsButtons'

export interface AspectOption {
	id: string
	ratio: number // e.g., 1.91, 1, or 3 for 3:1
	label: string
	targetWidth: number // Final output resolution
	targetHeight: number // Final output resolution
	displayWidth: number // Display container resolution
	displayHeight: number // Display container resolution
}

export const aspectOptions: AspectOption[] = [
	{
		id: 'option1',
		ratio: 1.91,
		label: '1.91:1',
		targetWidth: 1200,
		targetHeight: 628,
		displayWidth: 573,
		displayHeight: 300,
	},
	{
		id: 'option2',
		ratio: 1,
		label: '1:1',
		targetWidth: 600,
		targetHeight: 600,
		displayWidth: 600,
		displayHeight: 600,
	},
	{
		id: 'option3',
		ratio: 3,
		label: '3:1',
		targetWidth: 600,
		targetHeight: 200,
		displayWidth: 600,
		displayHeight: 200,
	},
]

// Interface for dimension variant images
export interface DimensionVariantImage {
	url: string
	width: number
	height: number
	isAspectRatioValid?: boolean // Indicates if the image matches the expected aspect ratio
}

export interface DimensionOptionsProps {
	selectedOption: AspectOption
	onSelect: (option: AspectOption) => void
	// Props for preview functionality
	imageCanvasRef?: React.RefObject<HTMLCanvasElement>
	containerWidth?: number
	showPreviews?: boolean
	// Modified prop for dimension-specific images
	dimensionImages?: Record<string, DimensionVariantImage>
	// Default image source as fallback
	defaultImageSrc?: string
	// Props for ref registration
	registerImageRef?: (dimensionId: string, element: HTMLImageElement | null) => void
	registerContainerRef?: (dimensionId: string, element: HTMLDivElement | null) => void
	// Prop for redirecting to adjust tools
	onSwitchToAdjustTool?: () => void
}

/**
 * Component for selecting image dimensions/aspect ratios
 * Renders either a preview mode with thumbnails or a button-based UI
 */
export const DimensionOptions: React.FC<DimensionOptionsProps> = ({
	selectedOption,
	onSelect,
	containerWidth,
	showPreviews = false,
	dimensionImages = {},
	defaultImageSrc = '',
	registerImageRef,
	registerContainerRef,
	onSwitchToAdjustTool,
}) => {
	// Render the preview mode if showPreviews is true and containerWidth is provided
	if (showPreviews && containerWidth) {
		return (
			<DimensionOptionsPreview
				selectedOption={selectedOption}
				onSelect={onSelect}
				containerWidth={containerWidth}
				dimensionImages={dimensionImages}
				defaultImageSrc={defaultImageSrc}
				registerImageRef={registerImageRef}
				registerContainerRef={registerContainerRef}
				onSwitchToAdjustTool={onSwitchToAdjustTool}
			/>
		)
	}

	// Otherwise, render the button-based mode
	return (
		<DimensionOptionsButtons
			selectedOption={selectedOption}
			onSelect={onSelect}
			dimensionImages={dimensionImages}
			registerContainerRef={registerContainerRef}
			onSwitchToAdjustTool={onSwitchToAdjustTool}
		/>
	)
}
